/**
 * Confirmação do cadastro (CERES-008b; CERES-ACC-005): a página do link do e-mail.
 *
 * Confirma no envio do formulário, e não no carregamento — clientes de e-mail
 * pré-visitam links (antivírus, prefetch), e um GET que confirmasse seria
 * acionado por um robô antes da pessoa. A senha escolhida no cadastro é pedida
 * de novo: é ela que impede alguém de cadastrar o seu e-mail com a senha DELE e
 * esperar você confirmar.
 */

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SimboloCeres } from '../components/Marca';
import { RodapePublico } from '../components/RodapePublico';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { guardarContinuacao, type Continuacao } from '../lib/cadastro-publico';
import { Field, InlineError, Spinner } from '../components/ui';

export function ConfirmarCadastroPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [senha, setSenha] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const confirmar = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const resposta = await api.post<Continuacao>('/auth/cadastro/confirmar', { token, senha });
      guardarContinuacao(resposta.continuacao);
      navigate('/criar-conta/empresa', { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sunken px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <SimboloCeres className="mb-3 h-11 w-11 text-brand-600" decorativa />
          <h1 className="text-xl font-semibold text-ink">{t('cadastro.confirmarTitulo')}</h1>
        </div>

        <div className="panel px-5 py-5">
          {token === '' ? (
            <p className="text-center text-sm text-ink-muted">{t('cadastro.semToken')}</p>
          ) : (
            <form onSubmit={(event) => void confirmar(event)} className="space-y-4" noValidate>
              <p className="text-sm text-ink-muted">{t('cadastro.confirmarTexto')}</p>
              <Field label={t('cadastro.senha')} htmlFor="senha" required>
                <input
                  id="senha"
                  type="password"
                  autoComplete="current-password"
                  required
                  autoFocus
                  className="field-input"
                  value={senha}
                  onChange={(event) => setSenha(event.target.value)}
                />
              </Field>
              <InlineError error={error} />
              <button type="submit" className="btn-primary w-full" disabled={busy || senha === ''}>
                {busy && <Spinner />}
                {t('cadastro.confirmarBotao')}
              </button>
            </form>
          )}
        </div>

        <p className="mt-4 text-center text-sm text-ink-muted">
          <Link to="/criar-conta/empresa" className="underline hover:text-ink">
            {t('cadastro.retomarTitulo')}
          </Link>
        </p>

        <RodapePublico />
      </div>
    </div>
  );
}
